import { useCallback, useEffect, useRef } from 'react';

import { useDialogs } from '@work4all/components';
import { IStackItem } from '@work4all/components/lib/navigation/history-stack';
import {
  NavigationOverlay,
  withOnCloseProvider,
} from '@work4all/components/lib/navigation/navigation-overlay';

import { CloneConvertDialog } from '../components/CloneConvertDialog';
import { CloneConvertDialogProps } from '../types';

export type CloneConvertDialogCallbacks = Omit<
  CloneConvertDialogProps,
  'onClose'
> &
  Partial<Pick<CloneConvertDialogProps, 'onClose'>>;
export const useCloneConvertDialogCallbacks = (
  props: Omit<CloneConvertDialogCallbacks, 'open'>
) => {
  const dialogs = useDialogs();
  const idRef = useRef<string>(null);

  const close = useCallback(() => {
    dialogs.close(idRef.current);
    idRef.current = '';
  }, [dialogs]);

  const open = useCallback(
    (overrides?: Partial<CloneConvertDialogProps>) => {
      if (idRef.current) return;

      const initialView: IStackItem = {
        view: (
          <CloneConvertDialog
            open
            {...props}
            {...overrides}
            onClose={() => {
              props.onClose?.();
              close();
            }}
            onConfirm={async (params, options) => {
              await props.onConfirm(params, options);
              close();
            }}
          />
        ),
      };
      const { id } = dialogs.open(withOnCloseProvider(NavigationOverlay), {
        initialView,
        close: () => {
          props.onClose?.();
          close();
        },
      });

      idRef.current = id;
      return () => {
        dialogs.close(id);
      };
    },
    [dialogs, props, close]
  );

  return { open, close };
};

export const useCloneConvertDialog = (
  props: Omit<CloneConvertDialogProps, 'open'>
) => {
  const { open, close } = useCloneConvertDialogCallbacks(props);
  useEffect(() => {
    if (props.targetEntity) open();
    else if (!props.targetEntity) close();
  }, [open, close, props.targetEntity]);
};
